import { Observable, of, ReplaySubject } from 'rxjs';

/**
 * How many chunks of rows the company tables keep, the shown and the
 * prefetched ones: enough to go back to a row a few thousand rows down
 * without loading the chunks above it again.
 */
export const PAGE_CACHE_CAPACITY = 30;

interface Entry<V> {
  value?: V;
  pending?: ReplaySubject<V>;
}

/**
 * Loaded pages by key, the least recently used one goes first once more than
 * `capacity` pages are kept. A page that is still loading is shared, so the
 * same page is never requested twice at a time. `clear()` drops every page and
 * every known total; a request that started before is not kept when it
 * answers later.
 *
 * Keys of the same query share a prefix (the query key), so the pages and the
 * total of one query can be dropped together.
 */
export class PageCache<V> {
  private readonly entries = new Map<string, Entry<V>>();
  private readonly totals = new Map<string, number>();
  private clears = 0;

  constructor(public readonly capacity = PAGE_CACHE_CAPACITY) {}

  /** Counts the `clear()` calls; rows loaded before a change are stale. */
  public get generation() {
    return this.clears;
  }

  /** Number of loaded pages, not counting the ones in flight. */
  public get size() {
    let size = 0;
    this.entries.forEach(
      (entry) => (size += entry.value !== undefined ? 1 : 0)
    );
    return size;
  }

  /** The loaded page; marks it as used. */
  public get(key: string): V | undefined {
    const entry = this.entries.get(key);
    if (entry?.value === undefined) {
      return undefined;
    }
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  public has(key: string) {
    return this.entries.get(key)?.value !== undefined;
  }

  public isPending(key: string) {
    return !!this.entries.get(key)?.pending;
  }

  /**
   * The page from the cache, the request in flight for it, or a new request.
   * The answer arrives synchronously with the response, like the request
   * itself would deliver it. A failed request is not kept. The request runs
   * to its end even when nobody listens any more, so it fills the cache.
   */
  public fetch(key: string, load: () => Observable<V>): Observable<V> {
    const entry = this.entries.get(key);
    if (entry?.value !== undefined) {
      return of(this.get(key) as V);
    }
    if (entry?.pending) {
      return entry.pending.asObservable();
    }
    const generation = this.clears;
    const pending = new ReplaySubject<V>(1);
    const created: Entry<V> = { pending };
    this.entries.set(key, created);
    let answered = false;
    load().subscribe({
      next: (value) => {
        if (answered) {
          return;
        }
        answered = true;
        if (generation === this.clears && this.entries.get(key) === created) {
          // Stored as the newest page.
          this.entries.delete(key);
          this.entries.set(key, { value });
          this.evict();
        }
        pending.next(value);
        pending.complete();
      },
      error: (e: unknown) => {
        if (this.entries.get(key) === created) {
          this.entries.delete(key);
        }
        pending.error(e);
      },
      complete: () => {
        if (!answered) {
          if (this.entries.get(key) === created) {
            this.entries.delete(key);
          }
          pending.complete();
        }
      },
    });
    return pending.asObservable();
  }

  /** The total of a query, as its first page reported it. */
  public total(queryKey: string) {
    return this.totals.get(queryKey);
  }

  public setTotal(queryKey: string, total: number) {
    this.totals.delete(queryKey);
    this.totals.set(queryKey, total);
    // A total per kept page is plenty; the oldest go first.
    for (const key of this.totals.keys()) {
      if (this.totals.size <= this.capacity) {
        break;
      }
      this.totals.delete(key);
    }
  }

  /** Drops the pages and the total of one query. */
  public drop(queryKey: string) {
    this.totals.delete(queryKey);
    [...this.entries.keys()]
      .filter((key) => key.startsWith(queryKey))
      .forEach((key) => this.entries.delete(key));
  }

  /** Drops everything, after the shown companies changed. */
  public clear() {
    this.clears++;
    this.entries.clear();
    this.totals.clear();
  }

  private evict() {
    let excess = this.size - this.capacity;
    for (const [key, entry] of this.entries) {
      if (excess <= 0) {
        break;
      }
      if (entry.value !== undefined) {
        this.entries.delete(key);
        excess--;
      }
    }
  }
}
